import { useMemo, useState } from 'react';
import {
  Card, Table, Button, Modal, Form, Input, InputNumber, Switch, DatePicker, Space, Tag,
  Typography, Popconfirm, Alert, App, Empty, Select, Result,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ArrowLeftOutlined, BookOutlined,
  ApartmentOutlined, LockOutlined, ThunderboltOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { catalogItemsApi, type CatalogItemDto } from '@api/catalogItemsApi';
import { metadataApi } from '@api/metadataApi';
import dayjs from 'dayjs';
import type { MetaAttributeDto } from '@/types/metadata';
import ReferencePicker from '@components/forms/ReferencePicker';
import { acqEntityNameToReferenceType } from '@components/forms/referenceTypes';

/**
 * Дисплейні назви well-known довідників — щоб у fallback-екрані 404 показати
 * «Валюти» замість сирого typeCode «currency». Якщо ключа немає — показуємо typeCode як є.
 * Це лиш UX-метадані, без впливу на CRUD логіку.
 */
const ACQ_WELL_KNOWN_TITLES: Record<string, string> = {
  currency: 'Валюти',
  organization: 'Організації',
  counterparty: 'Контрагенти',
  bank: 'Банки',
  bank_account: 'Банківські рахунки',
  nomenclature: 'Номенклатура',
  nomenclature_group: 'Групи номенклатури',
  nomenclature_unit: 'Одиниці виміру',
  warehouse: 'Склади',
  cost_item: 'Статті витрат',
  counterparty_contract: 'Договори контрагентів',
  individual: 'Фізичні особи',
  employee: 'Співробітники',
  position: 'Посади',
  fixed_asset: 'Основні засоби',
  intangible_asset: 'Нематеріальні активи',
  organization_department: 'Підрозділи організацій',
  price_type: 'Типи цін номенклатури',
  vat_rate: 'Ставки ПДВ',
};

const { Title, Text } = Typography;

interface DynamicCatalogItemsPageProps {
  /** Код типу довідника (з MetaDocumentType.Code, Kind=catalog). */
  typeCode: string;
  /** Колбек "Назад" — повертає у Конфігурування / Конструктор довідників. */
  onBack?: () => void;
}

/**
 * Sprint 20+ Refactor "Domain → Meta-каталоги", Етап 3.
 *
 * CRUD-сторінка записів meta-каталогу. Структура полів — динамічна,
 * рендериться з MetaAttribute відповідного типу. Підтримує:
 * - Пошук по DataJson (server-side substring)
 * - Hierarchical (через ParentId, для типів з IsHierarchical=true)
 * - Owner-фільтр (для типів з OwnerType — підпорядковані довідники)
 *
 * Системні снапшот-довідники (IsSystem=true) read-only — backend блокує POST/PUT/DELETE.
 */
export default function DynamicCatalogItemsPage({ typeCode, onBack }: DynamicCatalogItemsPageProps) {
  const acqQc = useQueryClient();
  const { message: acqMsg } = App.useApp();

  const [acqSearch, acqSetSearch] = useState('');
  const [acqPage, acqSetPage] = useState(1);
  const [acqPageSize, acqSetPageSize] = useState(50);
  const [acqEditOpen, acqSetEditOpen] = useState(false);
  const [acqEditing, acqSetEditing] = useState<CatalogItemDto | null>(null);
  const [acqForm] = Form.useForm();

  const acqTypeQuery = useQuery({
    queryKey: ['metadata', 'catalog-types', typeCode],
    queryFn: () => metadataApi.getCatalogType(typeCode),
  });

  const acqType = acqTypeQuery.data;
  const acqIsSystem = acqType?.isSystem ?? false;
  const acqAttrs: MetaAttributeDto[] = acqType?.attributes ?? [];

  const acqItemsQuery = useQuery({
    queryKey: ['catalog-items', typeCode, acqPage, acqPageSize, acqSearch],
    queryFn: () => catalogItemsApi.list(typeCode, {
      page: acqPage, pageSize: acqPageSize, search: acqSearch || undefined,
    }),
    enabled: !!acqType,
  });

  const acqCreateMut = useMutation({
    mutationFn: (acqJson: string) => catalogItemsApi.create({ typeCode, dataJson: acqJson }),
    onSuccess: () => { acqQc.invalidateQueries({ queryKey: ['catalog-items', typeCode] }); acqMsg.success('Створено'); acqSetEditOpen(false); },
    onError: (e: any) => acqMsg.error(e?.response?.data ?? 'Помилка створення'),
  });

  const acqUpdateMut = useMutation({
    mutationFn: ({ id, json }: { id: string; json: string }) =>
      catalogItemsApi.update(id, { dataJson: json }),
    onSuccess: () => { acqQc.invalidateQueries({ queryKey: ['catalog-items', typeCode] }); acqMsg.success('Оновлено'); acqSetEditOpen(false); },
    onError: (e: any) => acqMsg.error(e?.response?.data ?? 'Помилка оновлення'),
  });

  const acqDeleteMut = useMutation({
    mutationFn: (id: string) => catalogItemsApi.delete(id),
    onSuccess: () => { acqQc.invalidateQueries({ queryKey: ['catalog-items', typeCode] }); acqMsg.success('Видалено'); },
    onError: (e: any) => acqMsg.error(e?.response?.data ?? 'Помилка видалення'),
  });

  const acqHandleAdd = () => {
    if (acqIsSystem) { acqMsg.warning('Системний довідник — read-only'); return; }
    acqSetEditing(null);
    acqForm.resetFields();
    acqSetEditOpen(true);
  };

  const acqHandleEdit = (acqRow: CatalogItemDto) => {
    if (acqIsSystem) { acqMsg.warning('Системний довідник — read-only'); return; }
    acqSetEditing(acqRow);
    acqForm.resetFields();
    try {
      const acqData = JSON.parse(acqRow.dataJson || '{}');
      // Конвертуємо date-стрічки у dayjs для DatePicker
      acqAttrs.forEach((a) => {
        if ((a.dataType === 'date' || a.dataType === 'datetime') && acqData[a.code]) {
          acqData[a.code] = dayjs(acqData[a.code]);
        }
      });
      acqForm.setFieldsValue(acqData);
    } catch {
      acqForm.resetFields();
    }
    acqSetEditOpen(true);
  };

  const acqHandleSubmit = async () => {
    const acqValues = await acqForm.validateFields();
    // Конвертуємо dayjs назад у ISO string
    const acqData: Record<string, unknown> = { ...acqValues };
    acqAttrs.forEach((a) => {
      if ((a.dataType === 'date' || a.dataType === 'datetime') && acqData[a.code]) {
        acqData[a.code] = dayjs.isDayjs(acqData[a.code])
          ? (acqData[a.code] as dayjs.Dayjs).toISOString()
          : acqData[a.code];
      }
    });
    const acqJson = JSON.stringify(acqData);
    if (acqEditing) {
      await acqUpdateMut.mutateAsync({ id: acqEditing.id, json: acqJson });
    } else {
      await acqCreateMut.mutateAsync(acqJson);
    }
  };

  // Рендеримо тільки реквізити з header-секції (для довідників табличних частин зазвичай немає)
  const acqHeaderAttrs = useMemo(
    () => acqAttrs.filter((a) => !a.section || a.section === 'header'),
    [acqAttrs],
  );

  // Перші 4-5 атрибутів — у таблиці. Решта — лише у формі.
  const acqColumnAttrs = acqHeaderAttrs.slice(0, 5);

  if (acqTypeQuery.isLoading) {
    return <Empty description="Завантаження…" />;
  }
  if (acqTypeQuery.isError || !acqType) {
    return (
      <CatalogTypeMissing
        typeCode={typeCode}
        onBack={onBack}
        onCreated={() => acqQc.invalidateQueries({ queryKey: ['metadata', 'catalog-types', typeCode] })}
      />
    );
  }

  return (
    <>
      <Card style={{ marginBottom: 16 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
          <Space size={8}>
            {onBack && <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Назад</Button>}
            <Title level={4} style={{ margin: 0 }}><BookOutlined /> {acqType.name}</Title>
            <Text type="secondary" code>{typeCode}</Text>
            {acqIsSystem && (
              <Tag color="blue" icon={<LockOutlined />}>Системний (read-only)</Tag>
            )}
            {acqType.isHierarchical && (
              <Tag color="purple" icon={<ApartmentOutlined />}>Ієрархічний</Tag>
            )}
            {acqType.ownerType && (
              <Tag>Підпорядкований: {acqType.ownerType}</Tag>
            )}
            <Text type="secondary">{acqItemsQuery.data?.totalCount ?? 0} запис(ів)</Text>
          </Space>
          <Space>
            <Input
              placeholder="Пошук…"
              prefix={<SearchOutlined />}
              value={acqSearch}
              onChange={(e) => { acqSetSearch(e.target.value); acqSetPage(1); }}
              style={{ width: 220 }}
              allowClear
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={acqHandleAdd} disabled={acqIsSystem}>
              Новий запис
            </Button>
          </Space>
        </Space>
      </Card>

      <Table
        rowKey="id"
        size="small"
        loading={acqItemsQuery.isLoading}
        dataSource={acqItemsQuery.data?.items ?? []}
        pagination={{
          current: acqPage,
          pageSize: acqPageSize,
          total: acqItemsQuery.data?.totalCount ?? 0,
          onChange: (p, ps) => { acqSetPage(p); acqSetPageSize(ps); },
          pageSizeOptions: [25, 50, 100],
          showSizeChanger: true,
        }}
        columns={[
          ...acqColumnAttrs.map((a) => ({
            title: a.name,
            key: a.code,
            render: (_: unknown, acqRow: CatalogItemDto) => {
              try {
                const v = JSON.parse(acqRow.dataJson || '{}')[a.code];
                if (v == null || v === '') return <Text type="secondary">—</Text>;
                if (a.dataType === 'bool') return v ? <Tag color="green">так</Tag> : <Tag>ні</Tag>;
                if (a.dataType === 'date' || a.dataType === 'datetime') return dayjs(v).format('YYYY-MM-DD');
                return String(v);
              } catch { return ''; }
            },
          })),
          {
            title: 'Дії',
            key: 'actions',
            width: 110,
            render: (_, acqRow: CatalogItemDto) => acqIsSystem ? (
              <LockOutlined style={{ color: '#bfbfbf' }} />
            ) : (
              <Space size={2}>
                <Button type="link" size="small" icon={<EditOutlined />} onClick={() => acqHandleEdit(acqRow)}>
                  Редагувати
                </Button>
                <Popconfirm
                  title="Видалити запис?"
                  okText="Так" cancelText="Ні"
                  onConfirm={() => acqDeleteMut.mutateAsync(acqRow.id)}
                >
                  <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      {/* Edit / Create modal */}
      <Modal
        title={acqEditing ? `Редагувати: ${acqType.name}` : `Новий: ${acqType.name}`}
        open={acqEditOpen}
        onOk={acqHandleSubmit}
        onCancel={() => acqSetEditOpen(false)}
        confirmLoading={acqCreateMut.isPending || acqUpdateMut.isPending}
        okText="Зберегти"
        cancelText="Скасувати"
        width={680}
        destroyOnHidden
      >
        <Form form={acqForm} layout="vertical">
          {acqHeaderAttrs.length === 0 && (
            <Alert
              type="warning"
              message="Тип довідника не має реквізитів"
              description="Додай реквізити через Конструктор довідників (Реквізити → +)."
            />
          )}
          {acqHeaderAttrs.map((a) => (
            <Form.Item
              key={a.code}
              name={a.code}
              label={a.name}
              rules={a.isRequired ? [{ required: true, message: 'Обовʼязкове' }] : undefined}
              valuePropName={a.dataType === 'bool' ? 'checked' : 'value'}
            >
              {renderControl(a)}
            </Form.Item>
          ))}
        </Form>
      </Modal>
    </>
  );
}

function renderControl(a: MetaAttributeDto) {
  switch (a.dataType) {
    case 'bool':
      return <Switch />;
    case 'integer':
    case 'int':
      return <InputNumber style={{ width: '100%' }} />;
    case 'decimal':
      return <InputNumber style={{ width: '100%' }} step={0.01} />;
    case 'date':
    case 'datetime':
      return <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />;
    case 'reference':
      return <ReferencePickerAdapter referenceEntity={a.referenceEntity ?? null} />;
    case 'enum':
      return <Select placeholder="enum" />;
    default:
      return <Input />;
  }
}

/**
 * AntD Form.Item інжектить `value` + `onChange` у дочірній компонент. ReferencePicker
 * має префіксовані пропси (acqValue/acqOnChange) — тому потрібен адаптер. Резолвимо
 * MetaAttribute.referenceEntity (PascalCase: "AccountingWarehouse") у REFERENCE_TYPES key
 * (camelCase: "warehouse") через acqEntityNameToReferenceType. Якщо невідомий entity —
 * fallback Input з GUID (старий шлях).
 */
function ReferencePickerAdapter({
  referenceEntity,
  value,
  onChange,
}: {
  referenceEntity: string | null;
  value?: string | null;
  onChange?: (acqId: string | null) => void;
}) {
  const acqRefType = acqEntityNameToReferenceType(referenceEntity);
  if (!acqRefType) {
    return (
      <Input
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value || null)}
        placeholder={`reference: ${referenceEntity ?? '?'}`}
      />
    );
  }
  return (
    <ReferencePicker
      acqReferenceType={acqRefType}
      acqValue={value ?? null}
      acqOnChange={(acqId) => onChange?.(acqId)}
    />
  );
}

/**
 * Fallback для випадку, коли typeCode не існує у MetaCatalogType. Причини:
 * - користувач ще не запускав BAS Phase 0d (масовий імпорт каталогів);
 * - frontend має hardcoded route на well-known код (currency/counterparty/...), а
 *   ні Phase 0d не було, ні вручну не створено через Конструктор.
 *
 * Дає 2 шляхи виходу: «Швидко створити» (POST мінімального типу з реквізитами Код/Назва)
 * або порада запустити Phase 0d через Імпорт з BAS.
 */
function CatalogTypeMissing({
  typeCode,
  onBack,
  onCreated,
}: {
  typeCode: string;
  onBack?: () => void;
  onCreated: () => void;
}) {
  const { message: acqMsg } = App.useApp();
  const [acqLoading, acqSetLoading] = useState(false);
  const acqTitle = ACQ_WELL_KNOWN_TITLES[typeCode];

  const acqHandleQuickCreate = async () => {
    acqSetLoading(true);
    try {
      await metadataApi.createCatalogType({
        code: typeCode,
        name: acqTitle ?? typeCode,
        navGroup: 'Довідники',
        isHierarchical: false,
        ownerType: null,
        attributes: [
          { code: 'code', name: 'Код', dataType: 'string', isRequired: false, isIndexed: true, orderIdx: 0 },
          { code: 'name', name: 'Найменування', dataType: 'string', isRequired: true, isIndexed: false, orderIdx: 1 },
        ],
      });
      acqMsg.success(`Створено тип «${acqTitle ?? typeCode}»`);
      onCreated();
    } catch (e: unknown) {
      acqMsg.error(`Не вдалось створити: ${(e as Error).message}`);
    } finally {
      acqSetLoading(false);
    }
  };

  return (
    <Result
      status="404"
      icon={<BookOutlined style={{ color: '#1677ff' }} />}
      title={acqTitle ? `Довідник «${acqTitle}» ще не створено` : `Тип «${typeCode}» не знайдено`}
      subTitle={
        <Space direction="vertical" size={4} style={{ textAlign: 'left' }}>
          <Typography.Text type="secondary">
            У базі немає <Typography.Text code>MetaCatalogType</Typography.Text> з кодом{' '}
            <Typography.Text code>{typeCode}</Typography.Text>.
          </Typography.Text>
          <Typography.Text type="secondary">
            Швидко створити стандартний тип (Код + Найменування) — або імпортувати усю BAS-конфігурацію
            через <b>Налаштування → Імпорт з BAS → Phase 0d</b> (одразу 200+ типів з канонічними кодами).
          </Typography.Text>
        </Space>
      }
      extra={
        <Space>
          {onBack && (
            <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Назад</Button>
          )}
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={acqLoading}
            onClick={acqHandleQuickCreate}
          >
            Швидко створити «{acqTitle ?? typeCode}»
          </Button>
        </Space>
      }
    />
  );
}
